import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { useState } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { Trash } from 'lucide-react'
import useMutationDeleteBlacklistReport from '../../../../../api/hooks/useMutationDeleteBlacklistReport'
import { BlacklistQueryKeys } from '../../../../../keys'
import { useIsCreatedByCurrentUser } from '../../../../../hooks/useIsCreatedByCurrentUser'

interface BlacklistDeleteButtonProps {
	id: string
	accountNumber: string
	createdBy: string
}

export function BlacklistDeleteButton({
	id,
	accountNumber,
	createdBy,
}: BlacklistDeleteButtonProps) {
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()
	const canDelete = useIsCreatedByCurrentUser(createdBy)

	const { mutate: deleteBlacklistReport, isPending: isDeleting } =
		useMutationDeleteBlacklistReport()

	const handleDelete = () => {
		deleteBlacklistReport(
			{ id },
			{
				onSuccess: () => {
					toast.success('ลบรายการ Blacklist สำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [BlacklistQueryKeys.GetBlacklist],
					})
					setOpen(false)
				},
				onError: (error: any) => {
					toast.error(error?.response?.data?.message || 'เกิดข้อผิดพลาด')
				},
			},
		)
	}

	// Don't render anything if user can't delete
	if (!canDelete) {
		return null
	}

	return (
		<>
			<Button
				variant="ghost"
				size="icon"
				onClick={() => setOpen(true)}
				className="text-red-600 hover:text-red-700"
			>
				<Trash className="h-4 w-4" />
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>ยืนยันการลบ</DialogTitle>
					</DialogHeader>
					<div className="py-4">
						<p>คุณแน่ใจหรือไม่ที่จะลบรายการนี้ออกจาก Blacklist?</p>
						<div className="mt-2 text-sm text-muted-foreground">
							<p>เลขบัญชี: {accountNumber}</p>
							<p className="mt-1">การดำเนินการนี้ไม่สามารถย้อนกลับได้</p>
						</div>
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="outline" onClick={() => setOpen(false)}>
							ยกเลิก
						</Button>
						<Button
							variant="destructive"
							disabled={isDeleting}
							onClick={handleDelete}
						>
							{isDeleting ? 'กำลังลบ...' : 'ลบ'}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
